import React from 'react'
import { Formik, Form, FormikProps } from 'formik'
import { useNewTranslation } from 'hooks/index'
import i18n from 'config/i18n'
import * as yup from 'yup'
import { Flex, Box, Text } from 'rebass'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { SubmissionState, PartiposterProfile, SocialMediaAccount } from 'state/submissions'
import { TaskState, LINK_IN_BIO_TYPE_ENUM } from 'state/tasks'
import { TagCell } from 'components/Shared/MuiTable/Cell/TagCell'
import { UrlCell, UrlCellProps } from 'components/Shared/MuiTable/Cell/UrlCell'
import { ActionCell } from 'components/Shared/MuiTable/Cell/ActionCell'
import { MediaCell, MediaCellProps } from 'components/Shared/MuiTable/Cell/MediaCell'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { theme } from 'theme'
import Inputs from 'components/Inputs'
import { FollowerTierOption, COUNTRY_ENUM, PLATFORM_ENUM } from 'services/api/constants'
import { getFollowerTierDisplay } from 'services/numbers'
import RectangleButton from 'particles/Button/RectangleButton'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { GridColDefProps } from '../SimpleTable/constants'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'

export interface RowsProps {
    // Note: Row id is treated from MUI DataGridPro component as list key, don't use index for dynamic list key
    // issue related: https://gitlab.com/partipost-dev/engineering/backlog-items/-/issues/246
    id: string
    uuid: string
    socialMediaInfo: {
        profiles: SocialMediaAccount[]
    }
    screenshots: string[]
    viewCount: {
        value: string
        color?: string
    }
    linkInBioUrl: string
    partiposterTags: {
        profileData: PartiposterProfile
        submissionUuid: string
        campaignUuid: string
    }
    isMultiSelectMode: boolean
}

interface GetColumnsParams {
    showLinkInBio: boolean
    isMultiSelectMode: boolean
    canManageTag: boolean
    disableRequestEdit?: boolean
    disableApprove?: boolean
    isCampaignEnded?: boolean
}

export const getColumns = ({
    showLinkInBio,
    isMultiSelectMode,
    canManageTag,
    disableRequestEdit,
    disableApprove,
    isCampaignEnded,
}: GetColumnsParams): GridColDefProps[] => {
    return [
        {
            field: 'profile',
            type: 'string',
            headerName: i18n.t('submission.table.profile'),
            width: 160,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
            subFields: [
                {
                    field: 'name',
                    headerName: i18n.t('submission.table.nameLabel'),
                },
                {
                    field: 'gender',
                    headerName: i18n.t('submission.table.genderLabel'),
                },
                {
                    field: 'age',
                    headerName: i18n.t('submission.table.ageLabel'),
                },
            ],
        },
        {
            field: 'socialMediaInfo',
            type: 'string',
            headerName: i18n.t('submission.table.socialMediaInfo'),
            width: 180,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
            subFields: [
                {
                    field: FIELDS_TYPE.IG_USERNAME,
                    headerName: i18n.t('submission.table.instagramUsernameLabel'),
                },
            ],
        },
        {
            field: 'igFollowers',
            headerName: i18n.t('submission.table.followerRangeLabel'),
            width: 155,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            sortable: false,
        },
        {
            field: 'screenshots',
            headerName: i18n.t('submission.table.screenshotLabel'),
            width: 126,
            renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
            sortable: false,
            filterable: false,
        },
        {
            field: 'viewCount',
            headerName: i18n.t('submission.table.viewCountLabel'),
            width: 125,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        },
        ...(showLinkInBio
            ? [
                  {
                      field: 'linkInBioUrl',
                      type: 'string',
                      headerName: i18n.t('submission.table.linkInBioLabel'),
                      width: 170,
                      renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
        ...(canManageTag
            ? [
                  {
                      field: 'partiposterTags',
                      headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                      width: 240,
                      minWidth: 200,
                      renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
        ...(!isMultiSelectMode && !isCampaignEnded
            ? [
                  {
                      field: 'actions',
                      headerName: i18n.t('submission.table.actionsLabel'),
                      renderCell: (props: GridRenderCellParams) => (
                          <ActionCell
                              {...props}
                              disableRequestEdit={disableRequestEdit}
                              disableApprove={disableApprove}
                          />
                      ),
                      sortable: false,
                      filterable: false,
                  },
              ]
            : []),
    ]
}

export const getRows = (
    campaignUuid: string,
    submissions: SubmissionState[],
    profileById: Record<string, PartiposterProfile>,
    task: TaskState,
    followerTierOptions: FollowerTierOption[],
    isMultiSelectMode: boolean,
    country?: COUNTRY_ENUM,
): RowsProps[] => {
    return submissions.map(submission => {
        const profile = profileById[submission.partiposterId]
        const { social_media_accounts: socialMediaAccounts } = profile
        const igProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.IG,
        )[0]
        let linkInBioUrl
        if (task && task.data) {
            if (task.data.link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK) {
                linkInBioUrl = task.data['link_in_bio_url']
            } else if (task.data.link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK) {
                if (submission.submission_task != undefined) {
                    linkInBioUrl = submission.submission_task.data['link_in_bio_url']
                }
            }
        }
        return {
            id: submission.uuid,
            uuid: submission.uuid,
            profile: { profile },
            socialMediaInfo: { profiles: [igProfile] },
            igFollowers: {
                value:
                    igProfile && country && followerTierOptions
                        ? getFollowerTierDisplay(igProfile, country, followerTierOptions)
                        : '',
            },
            screenshots: submission.data && submission.data.files,
            viewCount: { value: submission.data && submission.data.view_count },
            linkInBioUrl: linkInBioUrl,
            partiposterTags: {
                profileData: profile,
                submissionUuid: submission.submission_uuid,
                campaignUuid: campaignUuid,
            },
            isMultiSelectMode: isMultiSelectMode,
        }
    })
}

export const ApproveSubmission: React.FC<any> = ({ handleApprove, handleClear, data }) => {
    const [newT] = useNewTranslation()
    const validation = yup.object({
        comment: yup
            .string()
            .nullable()
            .max(10000, ({ max }) =>
                newT(
                    'errorMessage.validation.enterLessThanCharacterLimit',
                    'Please enter less than {{max}} characters.',
                    {
                        max,
                    },
                ),
            ),
        data: yup.object({
            view_count: yup.number().required(),
        }),
    })

    return (
        <Formik
            initialValues={{
                comment: data.comment,
                data: {
                    files: data.data.files,
                    view_count: data.data.view_count,
                    posting_time: data.created_at,
                },
            }}
            enableReinitialize={true}
            validationSchema={validation}
            onSubmit={(values: { comment: string | null; data: any }, actions): void => {
                const comment = values.comment || newT('app.defaultApproveComment')
                handleApprove(data.uuid, comment, values.data)
                actions.setSubmitting(false)
            }}
        >
            {(formikBag: FormikProps<any>) => {
                return (
                    <Form>
                        <Box width={'252px'}>
                            <Inputs.Number
                                label={newT(
                                    'submission.review.editInstagramStoryViewHeader',
                                    "Edit the Instagram Story's view count",
                                )}
                                name="data.view_count"
                                inputSize="s"
                                required
                                width="100%"
                            />
                        </Box>
                        <Text fontSize="0.9em" textAlign="left" my={theme.spacing.xxxs}>
                            {newT('submission.review.commentsForPartiposterHeader', 'Comments for Partiposter')}
                        </Text>
                        <Text color={theme.colors.doveGrey} fontSize="0.6em" textAlign="left">
                            {newT('submission.review.commentsForPartiposterSubtext')}
                        </Text>
                        <Inputs.TextArea
                            name="comment"
                            placeholder={newT('placeholderText.enterCommentForPartiposter')}
                            minTextAreaHeight={64}
                            maxTextAreaHeight={332}
                        />
                        <Flex>
                            <RectangleButton
                                form="outlined"
                                size="l"
                                label={newT('button.cancel')}
                                onClick={handleClear}
                            />
                            <Box mx="auto" />
                            <RectangleButton form="filled" size="l" type="submit" label={newT('button.approve')} />
                        </Flex>
                    </Form>
                )
            }}
        </Formik>
    )
}
